import { SvgIconProps } from '@mui/material/SvgIcon';
import { LinkProps, matchPathname } from '@tanstack/react-router';

import { Permission, Platform, PlatformName, UserInfo } from '@/features/users';
import {
  Activity,
  FileSpreadsheet,
  Globe,
  Users,
} from '@/shared/components/icons';
import BarChartOutlinedIcon from '@/shared/components/icons/BarChartOutlinedIcon';
import HomeOutlinedIcon from '@/shared/components/icons/HomeOutlinedIcon';
import LayersOutlinedIcon from '@/shared/components/icons/LayersOutlinedIcon';

export interface ChildRoute {
  id: string;
  title: string;
  path: LinkProps['to'];
  permission?: Permission;
}

interface NavigationConfig {
  id: string;
  title: string;
  Icon: React.ComponentType<SvgIconProps>;
  path: LinkProps['to'];
  childRoutes?: ChildRoute[];
  permission: Permission;
}

const sharedRoutes: NavigationConfig[] = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    Icon: HomeOutlinedIcon,
    path: '/',
    permission: 'dashboardAccess',
  },
];

const sharedTailRoutes: NavigationConfig[] = [
  {
    id: 'changelog',
    title: 'Change Log',
    Icon: Activity,
    path: '/changelog',
    permission: 'changelogAccess',
  },
  {
    id: 'reporting',
    title: 'Reporting',
    Icon: BarChartOutlinedIcon,
    path: '/reporting',
    permission: 'reportingAccess',
  },
];

function buildMediaPlanRoute(includeMediaPlan = true): NavigationConfig[] {
  if (!includeMediaPlan) {
    return [];
  }

  return [
    {
      id: 'media-plans',
      title: 'Media Plans',
      Icon: FileSpreadsheet,
      path: '/media-plans' as const,
      permission: 'mediaPlansAccess' as const,
    },
    {
      id: 'audience-library',
      title: 'Audience Library',
      Icon: Users,
      path: '/audience-library' as const,
      permission: 'audienceLibraryAccess' as const,
    },
  ];
}

function buildTraffickingRoute(
  includeAdvertiser: boolean,
  includeCampaign = true,
  includeInsertionOrders = false,
  isAdGroup = true
): NavigationConfig {
  const childRoutes: ChildRoute[] = [
    ...(includeAdvertiser
      ? [
          {
            id: 'advertiser',
            title: 'Advertiser',
            path: '/trafficking/advertiser' as const,
          },
        ]
      : []),
    ...(includeCampaign
      ? [
          {
            id: 'campaign',
            title: 'Campaign',
            path: '/trafficking/campaign' as const,
          },
        ]
      : []),
    ...(includeInsertionOrders
      ? [
          {
            id: 'insertion-order',
            title: 'Insertion Orders',
            path: '/trafficking/insertion-order' as const,
          },
        ]
      : []),
    {
      id: 'ad-unit',
      title: isAdGroup ? 'Ad Group' : 'Ad Set',
      path: '/trafficking/ad-unit' as const,
    },
  ];

  return {
    id: 'trafficking',
    title: 'Trafficking',
    Icon: LayersOutlinedIcon,
    path: '/trafficking',
    permission: 'traffickingAccess',
    childRoutes,
  };
}

function buildAlgorithmToolsRoute(
  includeOfflineSalesOptimizer = true,
  includeStarvationBidder = true
): NavigationConfig {
  const childRoutes: ChildRoute[] = [
    ...(includeOfflineSalesOptimizer
      ? [
          {
            id: 'offline-sales-optimizer',
            title: 'Offline Sales Optimizer',
            path: '/algorithm-tools/offline-sales-optimizer' as const,
            permission: 'offlineSalesOptimizerAccess' as const,
          },
        ]
      : []),
    ...(includeStarvationBidder
      ? [
          {
            id: 'starvation-bidder',
            title: 'Starvation Bidder',
            path: '/algorithm-tools/starvation-bidder' as const,
            permission: 'starvationBidderAccess' as const,
          },
        ]
      : []),
  ];

  return {
    id: 'algorithm-tools',
    title: 'Algorithm Tools',
    Icon: Globe,
    path: '/algorithm-tools',
    permission: 'algorithmToolAccess',
    childRoutes,
  };
}

export const platformNavigationConfig: Record<
  PlatformName,
  NavigationConfig[]
> = {
  'The Trade Desk': [
    ...sharedRoutes,
    ...buildMediaPlanRoute(),
    buildTraffickingRoute(true),
    ...sharedTailRoutes,
    buildAlgorithmToolsRoute(),
  ],
  Meta: [
    ...sharedRoutes,
    ...buildMediaPlanRoute(),
    buildTraffickingRoute(false, true, false, false),
    ...sharedTailRoutes,
    buildAlgorithmToolsRoute(),
  ],
  DV360: [
    ...sharedRoutes,
    ...buildMediaPlanRoute(false),
    buildTraffickingRoute(false, false, true),
    ...sharedTailRoutes,
    buildAlgorithmToolsRoute(false),
  ],
  TikTok: [...sharedRoutes, buildTraffickingRoute(true), ...sharedTailRoutes],
};

const META_HIDDEN_ACCOUNT_IDS = new Set([
  '677961576356828',
  '665103870712294',
  '556503860353304',
  '1605662663007473',
  '1384139638490830',
  '1384067101831532',
  '1377355719173280',
]);

// Route ids hidden from the sidebar for the Meta accounts listed above.
const META_RESTRICTED_ACCOUNT_HIDDEN_ROUTE_IDS = new Set([
  'media-plans',
  'audience-library',
  'offline-sales-optimizer',
]);

export function shouldHideRouteInSidebar(
  routeId: string,
  platform: Platform['name'],
  accountId: string | null | undefined
) {
  return (
    platform === 'Meta' &&
    accountId != null &&
    META_HIDDEN_ACCOUNT_IDS.has(accountId) &&
    META_RESTRICTED_ACCOUNT_HIDDEN_ROUTE_IDS.has(routeId)
  );
}

export function isRouteAvailableInPlatform(
  platform: Platform['name'],
  path: string
) {
  const config = platformNavigationConfig[platform];

  for (const route of config) {
    if (route.childRoutes) {
      // Allow the parent path itself (e.g. /trafficking landing redirect)
      if (matchPathname(path, { to: route.path })) {
        return true;
      }
      // Only allow sub-paths that match a declared child route — prevents
      // platform-specific children (e.g. /trafficking/insertion-order) from
      // passing on platforms where they aren't listed.
      for (const childRoute of route.childRoutes) {
        if (matchPathname(path, { to: childRoute.path, fuzzy: true })) {
          return true;
        }
      }
      continue;
    }

    // Routes without child entries (e.g. /media-plans) use fuzzy so that
    // dynamic sub-paths like /media-plans/$id are also allowed.
    if (matchPathname(path, { to: route.path, fuzzy: true })) {
      return true;
    }
  }

  return false;
}

export function isRouteUserPermitted(
  platform: Platform['name'],
  path: string,
  permissions: UserInfo['permissions']
) {
  const config = platformNavigationConfig[platform];

  for (const route of config) {
    if (!matchPathname(path, { to: route.path, fuzzy: true })) {
      continue;
    }
    if (!permissions[route.permission]) {
      return false;
    }

    if (route.childRoutes) {
      for (const childRoute of route.childRoutes) {
        if (matchPathname(path, { to: childRoute.path, fuzzy: true })) {
          return !childRoute.permission || permissions[childRoute.permission];
        }
      }
    }

    return true;
  }

  return false;
}

export function getFirstPermittedRoute(
  platform: Platform['name'],
  permissions: UserInfo['permissions']
) {
  const config = platformNavigationConfig[platform];

  for (const route of config) {
    if (permissions[route.permission]) {
      return route.path;
    }
  }

  return null;
}
